import { type FC, memo } from "react";
import { Handle, type NodeProps, NodeToolbar, Position } from "reactflow";

import { type BadgeVariant, Badge } from "../common/Badge";
import { FloatingCard } from "../common/FloatingCard";

export interface TopologyNodeData {
  slug: string;
  role: string;
  framework: string | null;
  primaryLanguage: string | null;
  dependencyCount: number;
  dependentCount: number;
}

const roleBorderColors: Record<string, string> = {
  backend: "border-emerald-400 dark:border-emerald-500",
  database: "border-amber-400 dark:border-amber-500",
  frontend: "border-blue-400 dark:border-blue-500",
  library: "border-slate-400 dark:border-slate-500",
  service: "border-violet-400 dark:border-violet-500",
  unknown: "border-slate-300 dark:border-slate-600",
};

const roleBadgeVariants: Record<string, BadgeVariant> = {
  backend: "success",
  database: "warning",
  frontend: "info",
  library: "default",
  service: "default",
  unknown: "default",
};

export const TopologyNode: FC<NodeProps<TopologyNodeData>> = memo(
  ({ data, selected }) => {
    const borderColor = roleBorderColors[data.role] ?? roleBorderColors.unknown;
    const shortSlug = data.slug.includes("/")
      ? data.slug.split("/").pop()
      : data.slug;

    return (
      <>
        <NodeToolbar isVisible={selected} position={Position.Top} offset={12}>
          <FloatingCard>
            <div className="mb-2 font-mono text-xs font-bold">{data.slug}</div>
            <div className="space-y-1 text-xs">
              <div>
                Role: <span className="uppercase">{data.role}</span>
              </div>
              {data.framework && <div>Framework: {data.framework}</div>}
              {data.primaryLanguage && (
                <div>Language: {data.primaryLanguage}</div>
              )}
              <div>
                Dependencies: {data.dependencyCount} &middot; Dependents:{" "}
                {data.dependentCount}
              </div>
            </div>
          </FloatingCard>
        </NodeToolbar>

        <div
          data-flow-node="true"
          className={`relative min-w-[180px] rounded-none border-2 bg-white px-3 py-2.5 shadow-[4px_4px_0_rgba(15,23,42,0.08)] transition-all hover:shadow-[6px_6px_0_rgba(15,23,42,0.12)] dark:bg-arti-panel dark:shadow-[4px_4px_0_rgba(15,23,42,0.24)] dark:hover:shadow-[6px_6px_0_rgba(15,23,42,0.32)] ${borderColor}`}
        >
          <Handle
            type="target"
            position={Position.Left}
            className="!-ml-1.5 !h-3 !w-3 !rounded-none !border-none !bg-current"
          />

          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
              <span
                className="truncate font-mono text-sm font-bold text-slate-900 dark:text-white"
                title={data.slug}
              >
                {shortSlug}
              </span>
              <Badge
                size="sm"
                variant={roleBadgeVariants[data.role] ?? "default"}
              >
                {data.role}
              </Badge>
            </div>
            {(data.framework || data.primaryLanguage) && (
              <div className="flex items-center gap-2 font-mono text-[10px] text-slate-500 dark:text-slate-400">
                {data.primaryLanguage && <span>{data.primaryLanguage}</span>}
                {data.primaryLanguage && data.framework && (
                  <span>&middot;</span>
                )}
                {data.framework && <span>{data.framework}</span>}
              </div>
            )}
          </div>

          <Handle
            type="source"
            position={Position.Right}
            className="!-mr-1.5 !h-3 !w-3 !rounded-none !border-none !bg-current"
          />
        </div>
      </>
    );
  },
);

TopologyNode.displayName = "TopologyNode";

export const topologyNodeTypes = { topology: TopologyNode };
